import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useNavigate } from 'react-router-dom';
import {
  CTA,
  CustomInput,
  CustomSelect,
  Icon,
  Modal,
  NoResult,
  Text,
} from '../..';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import { Footer, ScrollableArea } from '../style';
import { MODAL_CLOSE } from '../../../../app/redux/actions';
import theme from '../../../theme';
import Pagination from '../../Pagination';
import { STATS_USER_LIST } from '../../../../app/redux/actions/statistics';
import SortBy from '../../SortBy';
import { FiltersWrapper, Legend, SortingWrapper, UserCard } from './style';
import routes from '../../../../routes';
import { timestampToDate } from '../../../../utils/common';
import {
  getFilterLabel,
  getFilterOptions,
  getSortingOptions,
} from '../../../../utils/labels';
import Inline from '../../Inline';
import TimeIntervalFilter from '../../TimeIntervalFilter';

export const ModalUsersGroupId = 'ModalUsersGroup';

const ModalUsersGroup = ({
  mediaIsPhone,
  list,
  filters,
  pager,
  getUsers,
  modalClose,
  group_id,
}) => {
  const scrollableAreaRef = useRef(null);
  const navigate = useNavigate();
  // filters
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchValue, setSearchValue] = useState();

  useEffect(() => {
    if (group_id) {
      setSelectedFilters({ group_ids: group_id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group_id]);

  const handleGetUsers = (page) => {
    const parsedFilters = {
      ...selectedFilters,
      page,
    };
    getUsers(parsedFilters);
  };

  useEffect(() => {
    if (Object.keys(selectedFilters)?.length > 0) handleGetUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const onClickSearch = () => {
    setSelectedFilters({ ...selectedFilters, search_text: searchValue });
  };

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    newFilters[key] = value?.value;
    setSelectedFilters(newFilters);
  };

  const loadMore = (page) => handleGetUsers(page);
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v });

  return (
    <Modal
      title="Lista utenti"
      modalId={ModalUsersGroupId}
      scrollableAreaRef={scrollableAreaRef}
      mbTitle={16}
    >
      <Text type="b3" mb={32}>
        Sezione dedicata agli utenti che hanno visibilità sui percorsi formativi
        di cui sei tutor.
      </Text>
      <FiltersWrapper>
        <CustomInput
          size={426}
          placeholder="Cosa vuoi cercare?..."
          isSearch
          value=""
          icon="search-sm"
          isClearable
          onChange={(v) => setSearchValue(v)}
          onClickIcon={onClickSearch}
          onKeyPress={(event) => {
            if (event.key === 'Enter') onClickSearch();
          }}
        />
        {(Object.keys(filters) || [])
          .filter((key) => key !== 'group_ids')
          .map((key, i) => (
            <CustomSelect
              size="s"
              key={`filter-${i}`}
              wrapperId={`filter-${key}`}
              placeholder={getFilterLabel(key)}
              options={getFilterOptions(filters, key)}
              onChange={(value) => handleOnChange({ key, value })}
              isClearable
            />
          ))}
        <TimeIntervalFilter
          callback={(start_date, end_date) => {
            setSelectedFilters({
              ...selectedFilters,
              start_date,
              end_date,
            });
          }}
        />
      </FiltersWrapper>
      <SortingWrapper>
        <Inline columnGap="16px" alignItems="flex-start">
          <Text type="b3">{pager?.total_items || 0} utenti </Text>
          <Icon
            type="users-01"
            stroke={theme().colors.textPrimary}
            clickable={false}
            size={24}
          />
        </Inline>
        <SortBy
          options={getSortingOptions([
            'access_asc',
            'access_desc',
            'alphabetical_asc',
            'alphabetical_desc',
          ])}
          onChange={(v) => {
            setSelectedFilters({ ...selectedFilters, sort_by: v?.value });
          }}
        />
      </SortingWrapper>
      {list?.length > 0 && (
        <Legend>
          <Text type="statistics-heading">Nome e Cognome</Text>
          <Text type="statistics-heading">Matricola</Text>
          <Text type="statistics-heading">Forza dell'ordine</Text>
          <Text type="statistics-heading">Ruolo</Text>
          <Text type="statistics-heading">Regione</Text>
          <Text type="statistics-heading">Provincia</Text>
          <Text type="statistics-heading">Ultimo accesso</Text>
        </Legend>
      )}
      <ScrollableArea
        ref={scrollableAreaRef}
        preserveShadows
        style={{ minHeight: '320px' }}
      >
        {!list?.length > 0 && <NoResult />}
        {(list || []).map((user, i) => (
          <UserCard key={`user-${i}`}>
            <Text
              type="h3"
              onClick={() => {
                navigate(`${routes.userStatistics.path}/${user?.uid}`);
                modalClose();
              }}
            >
              {user?.first_name} {user?.last_name}
            </Text>
            <Text type="b4">{user?.matricola || '-'}</Text>
            <Text type="b4">{user?.ffoo || '-'}</Text>
            <Text type="b4">{user?.work_role || '-'}</Text>
            <Text type="b4">{user?.region || '-'}</Text>
            <Text type="b4">{user?.province || '-'}</Text>
            <Text type="b4">
              {timestampToDate(user?.last_access, 'DD/MM/YYYY - HH:mm')}
            </Text>
          </UserCard>
        ))}
      </ScrollableArea>
      <Footer pt={40} pb={48}>
        {pager?.number_of_pages > 1 ? (
          <Pagination
            pb={0}
            pager={pager}
            callBack={loadMore}
            setItemsPerPage={setItemsPerPage}
          />
        ) : (
          <CTA type="secondary" btnText="Chiudi" onClick={modalClose} />
        )}
      </Footer>
    </Modal>
  );
};

ModalUsersGroup.propTypes = {
  //HOC: withMediaQueries
  mediaIsPhone: PropTypes.bool,
  // state
  list: PropTypes.array,
  filters: PropTypes.object,
  pager: PropTypes.object,
  group_id: PropTypes.number,
  // dispatch
  getUsers: PropTypes.func,
  modalClose: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const { list, filters, pager } = state.statistics.users;
      const { group_id } = state.app.modal.payload;
      return { list, filters, pager, group_id };
    },
    (dispatch) => ({
      modalClose: () => dispatch({ type: MODAL_CLOSE }),
      getUsers: (filters) =>
        dispatch({ type: STATS_USER_LIST._REQUEST, filters }),
    })
  ),
  withMediaQueries
)(ModalUsersGroup);
